import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Sidebar } from 'primereact/sidebar';
import { useAuth } from '../hooks/useAuth';
import Logo from './Logo';
import GlobalWorkflowManager from './GlobalWorkflowManager';
import WorkflowStatusBadge from './WorkflowStatusBadge';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [workflowManagerVisible, setWorkflowManagerVisible] = useState(false);
  const [activeWorkflows] = useState<any[]>([]); // Track active workflows

  // Build navigation items based on user status
  const navigationItems = [];

  // Only show protected features if user is approved
  if (user?.status === 'approved') {
    navigationItems.push(
      { path: '/app/dashboard', label: 'Dashboard', icon: 'pi pi-home' },
      { path: '/app/projects', label: 'Projects', icon: 'pi pi-folder' },
      { path: '/app/projects/new', label: 'New Project', icon: 'pi pi-plus' }
    );
  }

  // Add admin link if user is super admin
  if (user?.isSuperAdmin) {
    navigationItems.push({
      path: '/app/admin',
      label: 'Admin',
      icon: 'pi pi-shield',
    });
  }

  const isActivePath = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarStyle: React.CSSProperties = {
    position: 'fixed',
    width: '250px',
    height: '100vh',
    top: 0,
    left: 0,
    backgroundColor: 'white',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999
  };

  const mainContainerStyle: React.CSSProperties = {
    marginLeft: '250px',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    width: 'calc(100% - 250px)'
  };

  const topbarStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: '250px',
    right: 0,
    height: '70px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 2rem',
    zIndex: 998,
    width: 'calc(100% - 250px)'
  };

  const menuItemStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    margin: '0.25rem 1rem',
    borderRadius: '6px',
    textDecoration: 'none',
    color: isActive ? 'white' : '#374151',
    backgroundColor: isActive ? '#3B82F6' : 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar - Desktop */}
      <div style={sidebarStyle} className="hidden md:flex">
        <Link to="/" style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', textDecoration: 'none' }}>
          <Logo size="md" />
        </Link>

        <nav style={{ flex: 1, padding: '1rem 0', overflowY: 'auto' }}>
          {navigationItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              style={menuItemStyle(isActivePath(item.path))}
            >
              <i className={item.icon} style={{ marginRight: '0.75rem' }}></i>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Info at Bottom */}
        {user && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Avatar image={user.picture} label={user.name?.charAt(0)} shape="circle" size="normal" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                {/* Status Badge */}
                {user.status === 'pending' && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.125rem 0.5rem',
                    backgroundColor: '#fef3c7',
                    borderRadius: '9999px',
                    marginTop: '0.25rem'
                  }}>
                    <i className="pi pi-clock" style={{ fontSize: '0.625rem', color: '#f59e0b' }}></i>
                    <span style={{ fontSize: '0.625rem', color: '#92400e', fontWeight: 500 }}>Pending</span>
                  </div>
                )}
                {user.status === 'suspended' && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.125rem 0.5rem',
                    backgroundColor: '#fee2e2',
                    borderRadius: '9999px',
                    marginTop: '0.25rem'
                  }}>
                    <i className="pi pi-ban" style={{ fontSize: '0.625rem', color: '#dc2626' }}></i>
                    <span style={{ fontSize: '0.625rem', color: '#7f1d1d', fontWeight: 500 }}>Suspended</span>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Link
                to="/app/profile"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  color: '#374151',
                  backgroundColor: isActivePath('/app/profile') ? '#f3f4f6' : 'transparent',
                  fontSize: '0.875rem'
                }}
              >
                <i className="pi pi-user" style={{ marginRight: '0.5rem' }}></i>
                Profile Settings
              </Link>
              <Button label="Sign Out" icon="pi pi-sign-out" className="p-button-outlined p-button-sm w-full" onClick={handleLogout} />
            </div>
          </div>
        )}
      </div>

      {/* Main Container */}
      <div style={mainContainerStyle}>
        {/* Topbar */}
        <div style={topbarStyle}>
          <div className="md:hidden">
            <Button icon="pi pi-bars" className="p-button-text" onClick={() => setMobileMenuVisible(true)} />
          </div>
          <div className="hidden md:block">
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Research Automation Platform</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Global Workflow Status Badge */}
            <WorkflowStatusBadge
              activeWorkflows={activeWorkflows}
              onNavigateToWorkflow={(projectId) => navigate(`/app/projects/${projectId}/workflow`)}
            />
            
            {/* Workflow Manager Button */}
            <Button
              icon="pi pi-list"
              rounded
              text
              severity="secondary"
              onClick={() => setWorkflowManagerVisible(true)}
              tooltip="View all workflows"
              tooltipOptions={{ position: 'bottom' }}
            />
            
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.875rem' }}>
              <i className="pi pi-home" style={{ marginRight: '0.5rem' }}></i>Public Site
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <main style={{ flex: 1, marginTop: '70px' }}>
          <Outlet />
        </main>

        {/* Footer */}
        <footer style={{ padding: '1rem 2rem', borderTop: '1px solid #e5e7eb', backgroundColor: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>© 2026 Insights Research</span>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Link to="/privacy" style={{ fontSize: '0.875rem', color: '#6b7280', textDecoration: 'none' }}>Privacy</Link>
              <Link to="/terms" style={{ fontSize: '0.875rem', color: '#6b7280', textDecoration: 'none' }}>Terms</Link>
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Sidebar */}
      <Sidebar visible={mobileMenuVisible} position="left" onHide={() => setMobileMenuVisible(false)} style={{ width: '250px' }}>
        <Link to="/" style={{ display: 'block', padding: '1rem', borderBottom: '1px solid #e5e7eb', textDecoration: 'none' }} onClick={() => setMobileMenuVisible(false)}>
          <Logo size="md" />
        </Link>
        <nav style={{ padding: '1rem 0' }}>
          {navigationItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              style={menuItemStyle(isActivePath(item.path))}
              onClick={() => setMobileMenuVisible(false)}
            >
              <i className={item.icon} style={{ marginRight: '0.75rem' }}></i>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.label}</span>
            </Link>
          ))}
        </nav>
        {user && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Avatar image={user.picture} label={user.name?.charAt(0)} shape="circle" size="normal" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{user.name}</span>
                {/* Status Badge */}
                {user.status === 'pending' && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.125rem 0.5rem',
                    backgroundColor: '#fef3c7',
                    borderRadius: '9999px',
                    marginTop: '0.25rem'
                  }}>
                    <i className="pi pi-clock" style={{ fontSize: '0.625rem', color: '#f59e0b' }}></i>
                    <span style={{ fontSize: '0.625rem', color: '#92400e', fontWeight: 500 }}>Pending</span>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Link
                to="/app/profile"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  color: '#374151',
                  backgroundColor: isActivePath('/app/profile') ? '#f3f4f6' : 'transparent',
                  fontSize: '0.875rem'
                }}
                onClick={() => setMobileMenuVisible(false)}
              >
                <i className="pi pi-user" style={{ marginRight: '0.5rem' }}></i>
                Profile Settings
              </Link>
              <Button label="Sign Out" icon="pi pi-sign-out" className="p-button-outlined p-button-sm w-full" onClick={() => { handleLogout(); setMobileMenuVisible(false); }} />
            </div>
          </div>
        )}
      </Sidebar>

      {/* Global Workflow Manager Sidebar */}
      <GlobalWorkflowManager
        visible={workflowManagerVisible}
        onHide={() => setWorkflowManagerVisible(false)}
      />
    </div>
  );
};

export default Layout;
